import { useMemo } from 'react';

import { Edition, ProductKey } from '@/components/signup/utils/products';
import { defaultProductData } from '@/templates/try/cloud/signup/signup-page/src/utils/data-mappings';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { defaultProduct, Product, products } from './products';

export const useProductIntent = (location: string) =>
	useMemo(() => getProductIntent(location), [location]);

// eslint-disable-next-line complexity
export const getProductIntent = (location: string) => {
	let edition: Edition | undefined;
	let product: Product | undefined;
	let bundle: string | undefined;
	let url: URL;

	try {
		url = new URL(location);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
	} catch (e) {
		return { edition, product };
	}

	if (url.pathname.includes('/software/jira/product-discovery')) {
		product = {
			bundle: 'jira-product-discovery',
			description: 'Jira Product Discovery',
			key: ProductKey.JIRA_PRODUCT_DISCOVERY,
			name: 'jira-product-discovery',
		};
		edition = 'free';
	} else if (url.pathname.includes('/software/jira/service-management')) {
		product = {
			bundle: 'service-collection',
			description: 'Service Collection',
			key: ProductKey.SERVICE_COLLECTION,
			name: 'service-collection',
		};
		edition = 'free';
	} else if (url.pathname.includes('/software/jira')) {
		product = {
			bundle: 'jira-software',
			description: 'Jira',
			key: ProductKey.JIRA_SOFTWARE,
			name: 'jira',
		};
		edition = 'free';
	}

	if (url.pathname.includes('/software/confluence')) {
		product = {
			bundle: 'confluence',
			description: 'Confluence',
			key: ProductKey.CONFLUENCE,
			name: 'confluence',
		};
		edition = 'free';
	}

	if (url.pathname.includes('/software/loom')) {
		product = {
			bundle: 'loom',
			description: 'Loom',
			key: ProductKey.LOOM,
			name: 'loom',
		};
		edition = 'premium';
	}

	if (url.pathname.includes('/collections/teamwork')) {
		product = {
			bundle: 'teamwork-collection',
			description: 'Teamwork Collection',
			key: ProductKey.TEAMWORK_COLLECTION,
			name: 'teamwork-collection',
		};
		edition = 'premium';
	}

	const isSignupPage = url.pathname.endsWith('/try/cloud/signup');
	const isConfirmationPage = url.pathname.endsWith('/ondemand/signup/confirmation');

	if (isSignupPage || isConfirmationPage || window.IS_STORYBOOK) {
		// defaults for the signup page
		if (isSignupPage) {
			edition = 'free';
			product = defaultProduct;
			bundle = defaultProduct.bundle;
		}

		// determine product
		if (url.searchParams.has('product')) {
			const foundProduct = products.find(
				(product) => product.key === url.searchParams.get('product'),
			);
			if (foundProduct) {
				product = foundProduct;
				if (foundProduct.bundle) {
					bundle = foundProduct.bundle;
				}
			}
		} else if (url.searchParams.has('bundle')) {
			const bundleParam = url.searchParams.get('bundle') as string;

			// validate bundle
			const collections = ['service-collection', 'teamwork-collection'];
			const isValidBundle =
				defaultProductData[bundleParam] !== undefined || collections.includes(bundleParam);

			// only set bundle = param if valid param; this affects how we define product
			if (isValidBundle) {
				bundle = bundleParam;
			}

			product = products.find((product) => product.bundle === bundle);
		} else if (url.searchParams.has('products')) {
			product = products.find(
				(product) => product.key === url.searchParams.get('products')?.split(',')[0],
			);
		}

		// bxpkit developer signup bundle
		if (url.searchParams.get('developer')) {
			product = undefined;
			bundle = 'developer';
		}

		// opsgenie EOL
		if (bundle === 'opsgenie' && checkFeatureFlagGate(featureFlags.gwp6524OpsgenieSignupEol)) {
			bundle = 'jira-service-management';
		}

		// determine edition
		const editionParam = url.searchParams.get('edition');
		if (editionParam === 'free') {
			edition = 'free';
		} else if (editionParam === 'standard') {
			edition = 'standard';
		} else if (editionParam === 'premium') {
			edition = 'premium';
		}

		/**
		 * Handle rovo dev standard signup experience
		 */
		if (bundle === 'devai' && edition === 'standard') {
			product = {
				...product,
				description: 'Rovo Dev',
			} as Product & { description: 'Rovo Dev' };
		}
	}
	return { edition, product, bundle };
};
